import React from 'react'
import { Button, Badge } from 'react-bootstrap'
import { Bot, CheckCircle2, XCircle } from 'lucide-react'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useGetTagsQuery } from '../../../services/tagApi'
import { Tag } from '../../../types/models/tag'

interface ClassificationReviewCardProps {
  item: AnnotationDataBoundingBox
  isSelected: boolean
  showAcceptButton?: boolean
  showRejectButton?: boolean
  onAccept?: () => void
  onReject?: () => void
  onClick?: () => void
}

const ClassificationReviewCard: React.FC<ClassificationReviewCardProps> = ({
  item,
  isSelected,
  showAcceptButton = false,
  showRejectButton = false,
  onAccept,
  onReject,
  onClick,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const tag = tags?.tags?.find((t: Tag) => t.id === item.name || t.id === item.class_name)
  const tagName = tag?.name || item.class_name || 'Unknown'
  const tagColor = tag ? `#${tag.color.toString(16).padStart(6, '0')}` : '#6c757d'

  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        border: isSelected ? '2px solid #58a6ff' : '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        backgroundColor: '#161b22',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
      }}
    >
      <div className="p-3">
        {/* Tag Name with Color Indicator */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <div
            style={{
              width: '4px',
              height: '24px',
              backgroundColor: tagColor,
              borderRadius: '2px',
            }}
          />
          <span className="text-light fw-bold" style={{ fontSize: '1rem' }}>
            {tagName}
          </span>
          {item.ai_labeled && <Bot size={16} className="text-info flex-shrink-0" />}
        </div>

        {/* Confidence Score */}
        {item.confidence !== undefined && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Confidence
              </span>
              <span className="fw-semibold" style={{ fontSize: '0.875rem', color: '#58a6ff' }}>
                {Math.round(item.confidence * 100)}%
              </span>
            </div>
            <div
              style={{
                width: '100%',
                height: '6px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${item.confidence * 100}%`,
                  height: '100%',
                  backgroundColor: tagColor,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {(showAcceptButton || showRejectButton) && (
          <div className="d-flex gap-2">
            {showAcceptButton && onAccept && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onAccept()
                }}
                className="flex-grow-1"
                style={{ fontSize: '0.8125rem' }}
              >
                <CheckCircle2 size={14} className="me-1" />
                Accept
              </Button>
            )}
            {showRejectButton && onReject && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onReject()
                }}
                className="flex-grow-1"
                style={{ fontSize: '0.8125rem' }}
              >
                <XCircle size={14} className="me-1" />
                Reject
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClassificationReviewCard
